// src/utils/hydrationSafe.js
/**
 * Utilities to prevent hydration mismatches between server and client
 */

import { useState, useEffect } from 'react';

/**
 * Hook to safely detect if we're running on the client side
 * Prevents hydration mismatches by starting with false and updating after mount
 */
export const useIsClient = () => {
  const [isClient, setIsClient] = useState(false);
  
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  return isClient;
};

/**
 * Hook to safely get the current year for copyright notices
 * Prevents hydration mismatches by using a consistent year between server and client
 */
export const useSafeDate = () => {
  const [currentYear, setCurrentYear] = useState(2024); // Default fallback year
  
  useEffect(() => {
    // Update with actual year only after hydration
    setCurrentYear(new Date().getFullYear());
  }, []);
  
  return {
    getFullYear: () => currentYear
  };
};

/**
 * Hook to safely detect prerendering environment
 * Prevents hydration mismatches from navigator.userAgent checks
 */
export const useIsPrerendering = () => {
  const [isPrerendering, setIsPrerendering] = useState(false);
  
  useEffect(() => {
    // Only check on client side
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
      setIsPrerendering(true);
    }
  }, []);
  
  return isPrerendering;
};

/**
 * Hook to safely access browser APIs
 * Returns null during SSR, actual values after hydration
 */
export const useBrowserAPI = (apiName, fallback = null) => {
  const [apiValue, setApiValue] = useState(fallback);
  
  useEffect(() => {
    if (typeof window !== 'undefined' && window[apiName]) {
      setApiValue(window[apiName]);
    }
  }, [apiName]);
  
  return apiValue;
};

/**
 * Wrapper component to prevent hydration mismatches
 * Renders children only after hydration is complete
 */
export const ClientOnly = ({ children, fallback = null }) => {
  const isClient = useIsClient();
  
  if (!isClient) {
    return fallback;
  }
  
  return children;
};

/**
 * Safe wrapper for console.log during SSR/prerendering
 * Only logs on client side and not during prerendering
 */
export const safeLog = (...args) => {
  if (typeof window !== 'undefined' && 
      typeof navigator !== 'undefined' && 
      navigator.userAgent !== 'ReactSnap' &&
      process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

/**
 * Prevent hydration mismatches from random values
 * Returns consistent values during SSR and initial render
 */
export const useStableRandom = (seed = 1) => {
  const [randomValue, setRandomValue] = useState(() => {
    // Use a seeded pseudo-random value for consistency
    return ((seed * 9301 + 49297) % 233280) / 233280;
  });
  
  useEffect(() => {
    // Update to actual random value after hydration
    setRandomValue(Math.random());
  }, []);
  
  return randomValue;
};
